import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SxProps, Theme } from '@mui/material/styles';
import { MdOutlineSend } from 'react-icons/md';
import { ForgotPasswordSchema, TForgotPassword } from '@onesource/schemas';
import { useSendPasswordResetMutation } from '@onesource/services';
import { AxiosError } from 'axios';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';

const getErrorMessage = (error: Error): string => {
  if (error instanceof AxiosError && error.response?.data?.message) {
    return error.response.data.message;
  }
  return 'Failed to send password reset email';
};

export interface ForgotPasswordFormProps {
  /** URL Supabase will redirect the user to after clicking the email link */
  passwordResetRedirectUrl?: string;
  /** URL for the "Back to Login" link (default: '/auth/login') */
  loginUrl?: string;
  /** Custom title (default: "Forgot Password") */
  title?: string;
  /** Callback when the reset email is sent successfully */
  onSuccess?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Custom styles for the form card */
  sx?: SxProps<Theme>;
}

export const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({
  passwordResetRedirectUrl,
  loginUrl = '/auth/login',
  title = 'Forgot Password',
  onSuccess,
  onError,
  sx,
}) => {
  const mutation = useSendPasswordResetMutation();

  const { control, handleSubmit, watch } = useForm<TForgotPassword>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'all',
  });

  const watchedEmail = watch('email');

  const onSubmit = handleSubmit(({ email }) => {
    mutation.mutate(
      { email, redirectTo: passwordResetRedirectUrl },
      {
        onSuccess: () => onSuccess?.(),
        onError: err => onError?.(err instanceof Error ? err : new Error(String(err))),
      },
    );
  });

  // Success state
  if (mutation.isSuccess) {
    return (
      <Box sx={{ textAlign: 'center', ...sx }}>
        <Typography variant="h5" color="success.main" mb={2} fontWeight="bold">
          Check Your Email
        </Typography>
        <Typography color="text.secondary" mb={1}>
          We sent a password reset link to:
        </Typography>
        <Typography fontWeight={600} mb={3}>
          {watchedEmail}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Click the link in the email to reset your password.
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          If you don't see it in your inbox, please check your Spam or Updates folder (in Gmail) and mark the email as
          "Not Spam" so future emails arrive in your inbox.
        </Typography>
        <Link href={loginUrl} sx={{ fontWeight: 600 }}>
          Back to Login
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Typography variant="h5" textAlign="center" mb={1}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Enter your email address and we'll send you a link to reset your password.
      </Typography>

      <form onSubmit={onSubmit}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Email"
              type="email"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              margin="normal"
              autoComplete="email"
              autoFocus
            />
          )}
        />

        {mutation.isError && (
          <Typography color="error" sx={{ mt: 1 }} variant="body2">
            {getErrorMessage(mutation.error)}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={mutation.isPending}
            endIcon={!mutation.isPending ? <MdOutlineSend /> : undefined}
          >
            {mutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
          </Button>
        </Box>
      </form>

      <Box mt={2} textAlign="center">
        <Link href={loginUrl} sx={{ fontWeight: 600, fontSize: '14px' }}>
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};
